import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Layers,
  Briefcase,
  Key,
  Sparkles,
  Users,
  Smartphone,
  Zap,
  Check,
  Minus,
  Plus,
  Quote,
  DollarSign,
  CheckCircle2,
  Loader2,
  Clock3,
  Lock,
  Languages,
  GitBranch,
  Store,
} from 'lucide-react';
import { PANEL_BASE } from '../../config/paths';
import Reveal from '../../components/Reveal';
import SeoHead from '../../components/SeoHead';
import JsonLd from '../../components/JsonLd';
import { SITE_NAME, SITE_URL, absoluteUrl, DEFAULT_OG_IMAGE } from '../../config/seo';

const STATS_KEYS = [
  { value: '2.500+', labelKey: 'home.statsUsers', icon: Users },
  { value: '5.000+', labelKey: 'home.statsApps', icon: Smartphone },
  { value: '50.000+', labelKey: 'home.statsDeploys', icon: Zap },
];

const PARTNER_NAMES = ['TechCorp', 'AppFlow', 'DevHub', 'BuildPro', 'CodeBase', 'ShipIt', 'MetaApp', 'CloudNine'];

const FEATURES_KEYS = [
  { icon: Layers, titleKey: 'home.feature1Title', descKey: 'home.feature1Desc', span: 'lg:col-span-2' },
  { icon: Briefcase, titleKey: 'home.feature2Title', descKey: 'home.feature2Desc', span: 'lg:col-span-1' },
  { icon: Key, titleKey: 'home.feature3Title', descKey: 'home.feature3Desc', span: 'lg:col-span-1' },
  { icon: Sparkles, titleKey: 'home.feature4Title', descKey: 'home.feature4Desc', span: 'lg:col-span-2' },
];

const FAQ_KEYS = [
  { qKey: 'home.faq1Q', aKey: 'home.faq1A' },
  { qKey: 'home.faq2Q', aKey: 'home.faq2A' },
  { qKey: 'home.faq3Q', aKey: 'home.faq3A' },
  { qKey: 'home.faq4Q', aKey: 'home.faq4A' },
  { qKey: 'home.faq5Q', aKey: 'home.faq5A' },
  { qKey: 'home.faq6Q', aKey: 'home.faq6A' },
];

const LANGUAGE_CHIPS = ['EN', 'TR', 'DE', 'FR', 'ES', 'JA', '+34'];

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="rounded-xl border border-gray-200 dark:border-gray-700 glass-card overflow-hidden shadow-sm group">
      <summary className="w-full py-5 px-5 sm:px-6 flex items-center justify-between text-left gap-4 hover:bg-gray-50/60 dark:hover:bg-gray-800/40 transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <span className="font-medium text-gray-900 dark:text-white pr-2">{q}</span>
        <span className="shrink-0 w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 group-open:hidden">
          <Plus className="h-4 w-4" />
        </span>
        <span className="shrink-0 w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 items-center justify-center text-gray-700 dark:text-gray-300 hidden group-open:flex">
          <Minus className="h-4 w-4" />
        </span>
      </summary>
      <div className="px-5 sm:px-6 pb-5 pt-0 border-t border-gray-100 dark:border-gray-700">
        <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed pt-1">{a}</p>
      </div>
    </details>
  );
}

/** Small decorative visual per feature card — no real screenshots needed. */
function FeatureVisual({ index }: { index: number }) {
  const { t } = useTranslation();
  if (index === 0) {
    return (
      <div className="space-y-2">
        {[
          { name: 'BuildMate iOS', tag: 'v2.4.0', color: 'bg-emerald-500' },
          { name: 'BuildMate Android', tag: 'v2.3.1', color: 'bg-primary-500' },
        ].map((app) => (
          <div
            key={app.name}
            className="flex items-center gap-3 rounded-lg bg-white/70 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/70 px-3 py-2"
          >
            <span className={`w-2 h-2 rounded-full ${app.color}`} />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 flex-1 truncate">{app.name}</span>
            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{app.tag}</span>
          </div>
        ))}
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {t('home.featureVisualMetadataExport')}</span>
          <span className="font-mono">100%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div className="h-full w-full rounded-full bg-emerald-500" />
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 text-primary-500 animate-spin" /> {t('home.featureVisualAiTranslation')}</span>
          <span className="font-mono">63%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div className="h-full w-[63%] rounded-full bg-primary-500" />
        </div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-white/70 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/70 px-3 py-2.5">
        <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
        <span className="font-mono text-sm text-gray-600 dark:text-gray-400 tracking-widest truncate">•••• •••• •••• 91F2</span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {LANGUAGE_CHIPS.map((lang) => (
        <span
          key={lang}
          className="px-2.5 py-1 rounded-md text-xs font-semibold bg-white/70 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/70 text-gray-700 dark:text-gray-300"
        >
          {lang}
        </span>
      ))}
    </div>
  );
}

const PRO_MONTHLY_PRICE = 20;
/** Yearly = pay 10 months, get 2 free. */
const PRO_YEARLY_TOTAL = PRO_MONTHLY_PRICE * 10;
const PRO_YEARLY_EFFECTIVE_MONTHLY = Math.round((PRO_YEARLY_TOTAL / 12) * 100) / 100;

export default function Home() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const proPrice = billingPeriod === 'monthly' ? PRO_MONTHLY_PRICE : PRO_YEARLY_EFFECTIVE_MONTHLY;
  const proPeriod = t('home.perMonth');
  const proHref = billingPeriod === 'monthly' ? '/register?plan=pro&billing=monthly' : '/register?plan=pro&billing=yearly';

  const structuredData = useMemo(() => {
    const faqEntities = FAQ_KEYS.map(({ qKey, aKey }) => ({
      '@type': 'Question',
      name: t(qKey),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(aKey),
      },
    }));

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: DEFAULT_OG_IMAGE,
        email: 'support@buildmate.com',
        sameAs: [
          'https://twitter.com/buildmate',
          'https://www.linkedin.com/company/buildmate-studio',
          'https://www.instagram.com/buildmate.studio/',
          'https://github.com/buildmate-studio',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
        description: t('seo.home.description'),
        inLanguage: ['en', 'tr'],
        publisher: { '@type': 'Organization', name: SITE_NAME },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: SITE_NAME,
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web',
        url: SITE_URL,
        description: t('seo.home.description'),
        image: DEFAULT_OG_IMAGE,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Starter plan — free forever',
        },
        featureList: [
          'App Store Connect metadata management',
          'Google Play Console listing management',
          'AI store localization (40+ languages)',
          'Background jobs for export and upload',
          'Secure secrets management',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqEntities,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: absoluteUrl('/'),
          },
        ],
      },
    ];
  }, [t, i18n.language]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const hash = location.hash?.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="overflow-x-hidden">
      <SeoHead page="home" />
      <JsonLd id="home" data={structuredData} />

      <section className="aurora-hero relative px-4 sm:px-6 pt-14 sm:pt-20 pb-16 sm:pb-24 bg-white dark:bg-gray-900" aria-labelledby="home-hero-heading">
        <div className="absolute inset-0 grid-pattern pointer-events-none" />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 rounded-full glass-card px-3.5 py-1.5 mb-6 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-600" />
              </span>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('home.heroBadge')}</span>
            </div>
            <h1 id="home-hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1]">
              {t('home.heroTitle')}
              <br />
              <span className="gradient-text">{t('home.heroHighlight')}</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/register"
                className="btn btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:-translate-y-0.5 transition-all"
              >
                {t('home.ctaStart')}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#pricing"
                className="btn btn-secondary inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-xl"
              >
                <DollarSign className="h-4 w-4" />
                {t('home.ctaPricing')}
              </a>
            </div>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">{t('home.heroNoCreditCard')}</p>
          </div>

          {/* Product mockup — shows the actual problem being solved instead of a generic demo video */}
          <Reveal className="relative max-w-4xl mx-auto">
            <div className="absolute -top-4 -left-4 sm:-left-10 z-20 hidden sm:flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 shadow-xl">
              <Store className="h-4 w-4 text-emerald-500" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">{t('home.mockupBadgeLive')}</span>
            </div>
            <div className="absolute -bottom-4 -right-4 sm:-right-10 z-20 hidden sm:flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 shadow-xl">
              <Languages className="h-4 w-4 text-primary-500" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">{t('home.mockupBadgeAi')}</span>
            </div>

            <div className="mockup-window rounded-2xl overflow-hidden border shadow-2xl">
              <div className="mockup-titlebar flex items-center gap-2 px-4 py-3">
                <span className="mockup-dot bg-red-500/70" />
                <span className="mockup-dot bg-yellow-500/70" />
                <span className="mockup-dot bg-emerald-500/70" />
                <span className="ml-3 text-xs font-mono text-gray-400 truncate">{t('home.mockupUrl')}</span>
              </div>
              <div className="p-5 sm:p-8 space-y-3">
                {[
                  { titleKey: 'home.mockupJob1Title', statusKey: 'home.mockupJob1Status', state: 'done' },
                  { titleKey: 'home.mockupJob2Title', statusKey: 'home.mockupJob2Status', state: 'running' },
                  { titleKey: 'home.mockupJob3Title', statusKey: 'home.mockupJob3Status', state: 'queued' },
                ].map((job) => (
                  <div
                    key={job.titleKey}
                    className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3.5"
                  >
                    <span
                      className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                        job.state === 'done'
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : job.state === 'running'
                          ? 'bg-primary-500/15 text-primary-400'
                          : 'bg-gray-500/15 text-gray-400'
                      }`}
                    >
                      {job.state === 'done' && <CheckCircle2 className="h-4 w-4" />}
                      {job.state === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
                      {job.state === 'queued' && <Clock3 className="h-4 w-4" />}
                    </span>
                    <span className="flex-1 text-sm font-medium text-gray-200 truncate">{t(job.titleKey)}</span>
                    <span
                      className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        job.state === 'done'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : job.state === 'running'
                          ? 'bg-primary-500/10 text-primary-400'
                          : 'bg-gray-500/10 text-gray-400'
                      }`}
                    >
                      {t(job.statusKey)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900/50 border-y border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12">
            {STATS_KEYS.map(({ value, labelKey, icon: Icon }, i) => (
              <Reveal key={labelKey} delayMs={i * 100} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mb-4">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{value}</div>
                <div className="mt-1 text-gray-600 dark:text-gray-400 font-medium">{t(labelKey)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-white dark:bg-gray-800/30 overflow-hidden">
        <p className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-8">
          {t('home.partnersTitle')}
        </p>
        <div className="marquee-container max-w-6xl mx-auto">
          <div className="marquee-track flex gap-4">
            {[...PARTNER_NAMES, ...PARTNER_NAMES].map((name, i) => (
              <div
                key={`${name}-${i}`}
                className="marquee-item shrink-0 px-8 py-4 rounded-xl glass-card text-gray-600 dark:text-gray-400 font-semibold text-lg"
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800/30 scroll-mt-20">
        <Reveal className="max-w-4xl mx-auto text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            {t('home.featuresTitle')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {t('home.featuresSubtitle')}
          </p>
        </Reveal>
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
          {FEATURES_KEYS.map(({ icon: Icon, titleKey, descKey, span }, index) => (
            <Reveal key={titleKey} delayMs={index * 80} className={`${span} h-full`}>
              <div className="h-full rounded-2xl glass-card lift-card p-6 sm:p-7 flex flex-col shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-purple-500 text-white mb-5 shadow-lg shadow-primary-500/20 shrink-0">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t(titleKey)}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">{t(descKey)}</p>
                <div className="mt-5">
                  <FeatureVisual index={index} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-900 scroll-mt-20">
        <Reveal className="max-w-4xl mx-auto text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            {t('home.pricingTitle')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {t('home.pricingSubtitle')}
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{t('home.monthly')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={billingPeriod === 'yearly'}
              onClick={() => setBillingPeriod((p) => (p === 'monthly' ? 'yearly' : 'monthly'))}
              className="relative shrink-0 w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  billingPeriod === 'yearly' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingPeriod === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{t('home.yearly')}</span>
          </div>
        </Reveal>
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-6 sm:gap-8 items-stretch">
          <Reveal className="h-full">
            <div className="h-full rounded-2xl border-2 border-gray-200 dark:border-gray-700 glass-card p-6 sm:p-8 flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.starterName')}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">{t('home.free')}</span>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('home.starterDesc')}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {['home.starterFeature1', 'home.starterFeature2', 'home.starterFeature3'].map((k) => (
                  <li key={k} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
                    {t(k)}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mt-8 w-full py-3 rounded-xl font-medium text-center btn btn-secondary">
                {t('home.starterCta')}
              </Link>
              <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">{t('home.heroNoCreditCard')}</p>
            </div>
          </Reveal>
          <Reveal delayMs={100} className="h-full">
            <div className="glow-ring rounded-2xl h-full">
              <div className="relative rounded-2xl bg-white dark:bg-gray-800 p-6 sm:p-8 flex flex-col shadow-xl h-full sm:scale-[1.03]">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-primary-600 to-purple-600 text-white shadow-md">
                  {t('home.popular')}
                </span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.proName')}</h3>
                <div className="mt-4 min-h-[3.5rem]">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">${proPrice}</span>
                    <span className="text-gray-500 dark:text-gray-400">{proPeriod}</span>
                  </div>
                  <p className={`mt-1 text-xs text-gray-600 dark:text-gray-400 ${billingPeriod === 'yearly' ? '' : 'invisible'}`}>
                    {t('home.proYearlyBilledHint', { price: PRO_YEARLY_TOTAL })}
                  </p>
                </div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('home.proDesc')}</p>
                <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-50 dark:bg-primary-900/30 px-3 py-1 text-xs font-medium text-primary-700 dark:text-primary-300">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                  {t('home.proTrialBadge')}
                </span>
                <ul className="mt-6 space-y-3 flex-1">
                  {['home.proFeature1', 'home.proFeature2', 'home.proFeature3', 'home.proFeature4', 'home.proFeature5'].map((k) => (
                    <li key={k} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Check className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
                      {t(k)}
                    </li>
                  ))}
                </ul>
                <Link
                  to={proHref}
                  className="mt-8 w-full py-3 rounded-xl font-medium text-center btn btn-primary shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:-translate-y-0.5 transition-all"
                >
                  {t('home.proCta')}
                </Link>
                <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">{t('home.proTrialNote')}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delayMs={200} className="h-full">
            <div className="h-full rounded-2xl border-2 border-gray-200 dark:border-gray-700 glass-card p-6 sm:p-8 flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.enterpriseName')}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">{t('home.enterprisePrice')}</span>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('home.enterpriseDesc')}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {['home.enterpriseFeature1', 'home.enterpriseFeature2', 'home.enterpriseFeature3'].map((k) => (
                  <li key={k} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
                    {t(k)}
                  </li>
                ))}
              </ul>
              <Link to="/register?plan=enterprise" className="mt-8 w-full py-3 rounded-xl font-medium text-center btn btn-secondary">
                {t('home.enterpriseCta')}
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="max-w-5xl mx-auto mt-10 text-center">
          <Link
            to="/compare"
            className="inline-flex items-center gap-2 py-3 px-5 rounded-xl font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-700 transition-colors"
          >
            {t('home.comparePlans')}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section id="testimonials" className="py-20 sm:py-28 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900/50 scroll-mt-20 overflow-hidden">
        <Reveal className="max-w-4xl mx-auto text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            {t('home.testimonialsTitle')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {t('home.testimonialsSubtitle')}
          </p>
        </Reveal>

        {/* Üst row: arka plan, sağa kayan */}
        <div className="marquee-container mb-2 sm:mb-4" aria-hidden="true">
          <div className="marquee-track-right flex gap-4 sm:gap-6">
            {[...[1, 2, 3], ...[1, 2, 3]].map((i, idx) => (
              <div
                key={`top-${idx}`}
                className="shrink-0 w-[280px] sm:w-[320px] rounded-2xl glass-card p-5 sm:p-6 shadow-sm opacity-75 scale-[0.92]"
              >
                <Quote className="h-8 w-8 text-primary-200/80 dark:text-primary-800/50 mb-3" />
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-2">&ldquo;{t(`home.testimonial${i}Quote`)}&rdquo;</p>
                <div className="mt-3">
                  <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">{t(`home.testimonial${i}Author`)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t(`home.testimonial${i}Role`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Orta row: focus */}
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-6 sm:gap-8 relative z-10">
          {[1, 2, 3].map((i, idx) => (
            <Reveal key={i} delayMs={idx * 100}>
              <div className="rounded-2xl glass-card lift-card p-6 sm:p-8 shadow-lg">
                <Quote className="h-10 w-10 text-primary-200 dark:text-primary-800/50 mb-4" />
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">&ldquo;{t(`home.testimonial${i}Quote`)}&rdquo;</p>
                <div className="mt-5">
                  <p className="font-semibold text-gray-900 dark:text-white">{t(`home.testimonial${i}Author`)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t(`home.testimonial${i}Role`)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Alt row: arka plan, sola kayan */}
        <div className="marquee-container mt-2 sm:mt-4" aria-hidden="true">
          <div className="marquee-track flex gap-4 sm:gap-6">
            {[...[1, 2, 3], ...[1, 2, 3]].map((i, idx) => (
              <div
                key={`bottom-${idx}`}
                className="shrink-0 w-[280px] sm:w-[320px] rounded-2xl glass-card p-5 sm:p-6 shadow-sm opacity-75 scale-[0.92]"
              >
                <Quote className="h-8 w-8 text-primary-200/80 dark:text-primary-800/50 mb-3" />
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-2">&ldquo;{t(`home.testimonial${i}Quote`)}&rdquo;</p>
                <div className="mt-3">
                  <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">{t(`home.testimonial${i}Author`)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t(`home.testimonial${i}Role`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-800/30 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <Reveal className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 dark:bg-primary-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home.faqLabel')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                {t('home.faqTitle')}
              </h2>
              <p className="mt-4 text-gray-600 dark:text-gray-400 text-base leading-relaxed">
                {t('home.faqSupportText')}
                <a
                  href="mailto:support@buildmate.com"
                  className="text-primary-600 dark:text-primary-400 underline underline-offset-2 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  {t('home.faqSupportLink')}
                </a>
              </p>
            </Reveal>
            <div className="lg:col-span-7 space-y-3">
              {FAQ_KEYS.map(({ qKey, aKey }) => (
                <FaqItem key={qKey} q={t(qKey)} a={t(aKey)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="cta" className="aurora-hero relative py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-br from-primary-600 to-purple-700 dark:from-primary-700 dark:to-purple-900 scroll-mt-20">
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3.5 py-1.5 mb-6">
            <GitBranch className="h-3.5 w-3.5 text-white/90" />
            <span className="text-xs font-medium text-white/90">{t('home.heroNoCreditCard')}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            {t('home.ctaSectionTitle')}
          </h2>
          <p className="mt-4 text-lg text-primary-100">
            {t('home.ctaSectionSubtitle')}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-medium bg-white text-primary-600 hover:bg-primary-50 hover:-translate-y-0.5 transition-all shadow-lg"
            >
              {t('home.ctaRegister')}
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to={PANEL_BASE}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-medium text-white border-2 border-white/80 hover:bg-white/10 transition-colors"
            >
              {t('home.ctaGoPanel')}
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
